import React, { forwardRef, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import he from 'date-fns/locale/he';
import arSA from 'date-fns/locale/ar-SA';
import enUS from 'date-fns/locale/en-US';
import {
    Autocomplete,
    Checkbox,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Popper,
    Portal,
    TableContainer,
    TextField,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './XtramModal.module.scss';

const cx = (...classes) => classes.filter(Boolean).join(' ');

export const ComboPopper = forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));

/**
 * The Xtram modal frame of JournalEntryTemplatesModal: header with the close icon, optional tabs, a scrolling
 * body and a footer for the actions. `wide` fits multi-column forms; `expanded` widens it for previews and tables.
 * `onOpened(node)` runs once per opening with the modal's root node; `onKeyDown` receives keys from anywhere inside.
 */
const XtramModal = ({
    open,
    onClose,
    title,
    direction = 'rtl',
    wide = false,
    expanded = false,
    busy = false,
    tabs,
    activeTab,
    onTabChange,
    footer,
    onKeyDown,
    onOpened,
    children,
}) => {
    const { t } = useTranslation();
    const onOpenedRef = useRef(onOpened);
    onOpenedRef.current = onOpened;
    const rootRef = useCallback((node) => {
        if (node) onOpenedRef.current?.(node);
    }, []);
    if (!open) return null;

    const close = () => {
        if (!busy) onClose?.();
    };

    return (
        <Portal>
            <div className={cx(styles.overlay, direction === 'rtl' ? styles.rtl : styles.ltr)} onClick={close}>
                <div ref={rootRef} dir={direction} onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
                    <div className={cx(styles.modal, wide && styles.modalWide, expanded && styles.modalEdit)}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton onClick={close} className={styles.closeIcon} disabled={busy}>
                                            <CancelIcon className={styles.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={styles.headerTitle}>{title}</div>
                                <div className={styles.headerSpacer} />
                            </div>

                            {tabs?.length > 0 && (
                                <div className={styles.tabs}>
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            disabled={tab.disabled}
                                            className={cx(styles.tab, activeTab === tab.key && styles.tabActive)}
                                            onClick={() => onTabChange?.(tab.key)}
                                        >
                                            {tab.label}
                                            {tab.count != null && (
                                                <span className={cx(styles.tabCount, tab.alert && styles.tabCountAlert)}>
                                                    {tab.count}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className={styles.scroll} data-xtram-scroll>{children}</div>

                            {footer && <div className={styles.footer}>{footer}</div>}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export const XtramButton = ({ variant = 'primary', small = false, active = false, busy = false, children, className, ...props }) => (
    <button
        type="button"
        className={cx(
            variant === 'primary' && styles.primaryBtn,
            variant === 'secondary' && styles.secondaryBtn,
            variant === 'ghost' && styles.ghostBtn,
            variant === 'danger' && styles.dangerBtn,
            active && styles.ghostBtnActive,
            small && styles.smallBtn,
            className
        )}
        {...props}
    >
        {busy ? <CircularProgress size={18} color="inherit" /> : children}
    </button>
);

export const XtramField = ({ label, required = false, hint, error, size, className, children }) => (
    <div className={cx(
        styles.formField,
        size === 'narrow' && styles.formFieldNarrow,
        size === 'wide' && styles.formFieldWide,
        size === 'full' && styles.formFieldFull,
        className
    )}>
        {label && (
            <label className={styles.fieldLabel}>
                {label}
                {required && <span className={styles.requiredMark}> *</span>}
            </label>
        )}
        {children}
        {error ? <p className={styles.fieldError}>{error}</p> : hint && <p className={styles.fieldHint}>{hint}</p>}
    </div>
);

export const XtramInput = ({ direction = 'rtl', error = false, startIcon, inputProps, InputProps, className, ...props }) => (
    <TextField
        variant="standard"
        fullWidth
        {...props}
        className={cx(fieldStyles.styleInput, error && fieldStyles.styleInputRequired, className)}
        inputProps={{ ...inputProps, style: { textAlign: direction === 'rtl' ? 'right' : 'left', ...inputProps?.style } }}
        InputProps={{
            disableUnderline: true,
            ...(startIcon ? { startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment> } : {}),
            ...InputProps,
        }}
    />
);

export const XtramSearch = ({ direction, value, onChange, placeholder, className }) => (
    <XtramInput
        direction={direction}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        startIcon={<SearchIcon fontSize="small" />}
        className={className}
    />
);

const defaultOptionLabel = (option) => option?.label ?? '';
const defaultOptionEqual = (a, b) => a?.value === b?.value;

/** The RTL-aware Autocomplete of JournalEntryTemplatesModal, for single or multiple values. */
export const XtramSelect = ({
    direction = 'rtl',
    options,
    value,
    onChange,
    placeholder = '',
    multiple = false,
    error = false,
    disabled = false,
    disableClearable = false,
    getOptionLabel = defaultOptionLabel,
    isOptionEqualToValue = defaultOptionEqual,
    filterOptions,
    className,
}) => {
    const renderPopper = useCallback((props) => <ComboPopper {...props} direction={direction} />, [direction]);

    return (
        <Autocomplete
            className={cx(fieldStyles.combo, multiple && styles.comboMulti, className)}
            size="small"
            fullWidth
            multiple={multiple}
            limitTags={multiple ? 1 : undefined}
            disabled={disabled}
            disableClearable={disableClearable && !multiple}
            options={options || []}
            value={value ?? (multiple ? [] : null)}
            onChange={(_, next) => onChange(multiple ? (next ?? []) : (next ?? null))}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            filterOptions={filterOptions}
            disableCloseOnSelect={multiple}
            ChipProps={{ size: 'small' }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={multiple && value?.length ? '' : placeholder}
                    className={cx(fieldStyles.styleInput, error && fieldStyles.styleInputRequired)}
                    inputProps={{ ...params.inputProps, style: { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return <li key={key} {...optionProps}>{getOptionLabel(option)}</li>;
            }}
            ListboxProps={{ direction }}
            PopperComponent={renderPopper}
        />
    );
};

const DATE_LOCALES = { he, ar: arSA };

const toPickerDate = (value) => {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** The dd/MM/yyyy DatePicker of EditModal, styled like XtramInput. Emits a local Date, or null when cleared. */
export const XtramDatePicker = ({ value, onChange, error = false, disabled = false, className }) => {
    const { i18n } = useTranslation();

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={DATE_LOCALES[i18n.language] || enUS}>
            <DatePicker
                value={toPickerDate(value)}
                onChange={(date) => {
                    if (date === null) onChange(null);
                    else if (!Number.isNaN(date.getTime())) onChange(date);
                }}
                format="dd/MM/yyyy"
                disabled={disabled}
                slotProps={{
                    textField: {
                        variant: 'standard',
                        fullWidth: true,
                        className: cx(fieldStyles.styleInput, error && fieldStyles.styleInputRequired, className),
                        InputProps: { disableUnderline: true },
                    },
                    popper: { sx: { zIndex: 1700 } },
                }}
            />
        </LocalizationProvider>
    );
};

export const XtramCheckbox = ({ checked, onChange, label, disabled = false }) => (
    <label className={styles.checkboxRow}>
        <Checkbox size="small" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
    </label>
);

export const XtramTable = ({ height, children }) => (
    <div className={styles.tableWrap}>
        <TableContainer
            component={Paper}
            variant="outlined"
            className={cx(height === 'tall' && styles.tableTall, height === 'medium' && styles.tableMedium)}
        >
            {children}
        </TableContainer>
    </div>
);

export const XtramNotice = ({ type = 'info', children }) => (
    <div className={cx(
        styles.notice,
        type === 'info' && styles.noticeInfo,
        type === 'warning' && styles.noticeWarning,
        type === 'error' && styles.noticeError,
        type === 'success' && styles.noticeSuccess
    )}>
        {children}
    </div>
);

/** A labelled figure; with `onClick` it is a button (e.g. a filter), `active` marking the chosen one. */
export const XtramStat = ({ label, value, tone, onClick, active = false }) => {
    const Root = onClick ? 'button' : 'div';
    return (
        <Root
            {...(onClick ? { type: 'button', onClick, 'aria-pressed': active } : {})}
            className={cx(
                styles.stat,
                tone === 'warning' && styles.statWarning,
                tone === 'error' && styles.statError,
                tone === 'success' && styles.statSuccess,
                onClick && styles.statClickable,
                active && styles.statActive
            )}
        >
            <span className={styles.statLabel}>{label}</span>
            <span className={styles.statValue}>{value}</span>
        </Root>
    );
};

export const XtramBadge = ({ tone, children }) => (
    <span className={cx(
        styles.badge,
        tone === 'success' && styles.badgeSuccess,
        tone === 'warning' && styles.badgeWarning,
        tone === 'error' && styles.badgeError,
        tone === 'info' && styles.badgeInfo
    )}>
        {children}
    </span>
);

export { styles as xtramStyles };

export default XtramModal;
